import { useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useTransport, createConnectQueryKey } from '@connectrpc/connect-query'
import { keepPreviousData, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { create, type MessageInitShape } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useStore } from '@tanstack/react-store'
import { Code, ConnectError } from '@connectrpc/connect'

import {
  listJobs,
  getPipelineSummary,
  getJob,
  getInvoiceLitesByIds,
  getQuoteLitesByIds,
  listJobActivity,
  listJobEvents,
  updateJob,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetPipelineSummaryRequestSchema,
  GetPipelineSummaryResponseSchema,
  GetJobRequestSchema,
  GetJobResponseSchema,
  JobListFiltersSchema,
  ListJobEventsRequestSchema,
  ListJobsResponseSchema,
  UpdateJobRequestSchema,
  type GetJobResponse,
  type GetPipelineSummaryResponse,
  type JobLite,
  type ListJobsResponse,
} from '@/gen/cortado/v1/jobs_pb'
import { GetInvoiceLitesByIdsRequestSchema, GetQuoteLitesByIdsRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import {
  InquiryRejectReasonEnum,
  InquiryStageEnum,
  JobBoardStageEnum,
  JobPhaseEnum,
  JobSortDirectionEnum,
  JobSortFieldEnum,
  JobStatusEnum,
  type JobPaymentStateEnum,
} from '@/gen/cortado/v1/settings_pb'
import { organizationStore } from './organization.store'
import { dateToTimestamp } from '@/lib/date-utils'
import { useUpdateInquiry } from './leads.store'

/** Server-enforced max page size for ListJobs (see backend handler clamp). */
export const PIPELINE_PAGE_SIZE = 100

/** Page size for a single board column (each column paginates independently). */
export const PIPELINE_BOARD_COLUMN_PAGE_SIZE = 25

export interface PipelineFilterOptions {
  /** Server-side text search over contact name / company / job title / quote #. */
  search?: string
  phases?: JobPhaseEnum[]
  paymentStates?: JobPaymentStateEnum[]
  boardStages?: JobBoardStageEnum[]
  assignedToId?: string
  eventStart?: Date
  eventEnd?: Date
  sortField?: JobSortFieldEnum
  sortDirection?: JobSortDirectionEnum
}

function buildJobListFilters(options?: PipelineFilterOptions): MessageInitShape<typeof JobListFiltersSchema> {
  return {
    phases: options?.phases ?? [],
    paymentStates: options?.paymentStates ?? [],
    boardStages: options?.boardStages ?? [],
    assignedToId: options?.assignedToId || undefined,
    eventStart: options?.eventStart ? dateToTimestamp(options.eventStart) : undefined,
    eventEnd: options?.eventEnd ? dateToTimestamp(options.eventEnd) : undefined,
    search: options?.search || undefined,
  }
}

/**
 * Shared offset-paginated ListJobs query. `useInfinitePipelineJobs` (the
 * table's single list) and `useInfinitePipelineBoardColumn` (one per board
 * column) both build on this — same shape, different page size and filters.
 */
function useInfiniteJobsQuery(options: PipelineFilterOptions | undefined, pageSize: number) {
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  // Init shape (not a created message) so `offset` is a definite number, which
  // connect-query's useInfiniteQuery requires for the page-param field.
  const input = useMemo(
    () => ({
      organizationId: organizationId || '',
      filters: buildJobListFilters(options),
      sortField: options?.sortField ?? JobSortFieldEnum.JOB_SORT_FIELD_UNSPECIFIED,
      sortDirection: options?.sortDirection ?? JobSortDirectionEnum.JOB_SORT_DIRECTION_UNSPECIFIED,
      limit: pageSize,
      offset: 0,
    }),
    [
      organizationId,
      pageSize,
      options?.search,
      options?.phases,
      options?.paymentStates,
      options?.boardStages,
      options?.assignedToId,
      options?.eventStart,
      options?.eventEnd,
      options?.sortField,
      options?.sortDirection,
    ],
  )

  const query = useInfiniteQuery(listJobs, input, {
    enabled: !!organizationId,
    pageParamKey: 'offset',
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.jobs.length, 0)
      return loaded < lastPage.totalCount ? loaded : undefined
    },
    placeholderData: keepPreviousData,
  })

  // Dedupe by id: cross-page shifts (jobs changing stage between fetches) can
  // re-deliver a row from an earlier page.
  const jobs = useMemo(() => {
    const seen = new Set<string>()
    const rows: JobLite[] = []
    for (const page of query.data?.pages ?? []) {
      for (const job of page.jobs) {
        if (seen.has(job.id)) continue
        seen.add(job.id)
        rows.push(job)
      }
    }
    return rows
  }, [query.data])
  const totalCount = query.data?.pages.at(-1)?.totalCount ?? 0

  return {
    jobs,
    totalCount,
    hasMore: jobs.length < totalCount,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Paginated (offset-based) Pipeline job list for the `/pipeline` table view.
 *
 * Mirrors {@link useInfiniteLeadsList} / `useInfiniteInvoicesList`: fetches in
 * pages of {@link PIPELINE_PAGE_SIZE} and exposes `fetchNextPage`/`hasMore` for
 * a "Load more" affordance.
 */
export function useInfinitePipelineJobs(options?: PipelineFilterOptions) {
  return useInfiniteJobsQuery(options, PIPELINE_PAGE_SIZE)
}

/**
 * One board column's independently-paginated job list — same shared filters
 * (search/owner/event date/sort) as the table, plus a fixed `board_stages`
 * filter for this column, at {@link PIPELINE_BOARD_COLUMN_PAGE_SIZE} per page.
 */
export function useInfinitePipelineBoardColumn(
  stage: JobBoardStageEnum,
  options?: Omit<PipelineFilterOptions, 'phases' | 'paymentStates' | 'boardStages'>,
) {
  return useInfiniteJobsQuery({ ...options, boardStages: [stage] }, PIPELINE_BOARD_COLUMN_PAGE_SIZE)
}

/**
 * Phase- and stage-bucket rollups for the Pipeline phase tiles and board
 * column headers.
 *
 * Always requests every phase/stage (ignores the list page's active tile
 * filter) so every tile/column shows its true total regardless of which is
 * selected.
 */
export function usePipelineSummary(options?: Omit<PipelineFilterOptions, 'phases' | 'boardStages'>) {
  const organizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(
    () =>
      create(GetPipelineSummaryRequestSchema, {
        organizationId: organizationId || '',
        filters: buildJobListFilters(options),
      }),
    [organizationId, options?.search, options?.assignedToId, options?.eventStart, options?.eventEnd],
  )

  const query = useQuery(getPipelineSummary, request, {
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  })

  return {
    buckets: query.data?.buckets ?? [],
    stageBuckets: query.data?.stageBuckets ?? [],
    balanceDueCents: query.data?.balanceDueCents ?? 0n,
    openInvoiceCount: query.data?.openInvoiceCount ?? 0,
    overdueInvoiceCount: query.data?.overdueInvoiceCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Lazily loads the Quoted/paid popover's breakdown for one job: `GetJob`
 * (for its `invoice_ids` and `quote_ids`) then `GetInvoiceLitesByIds` and
 * `GetQuoteLitesByIds` for the invoices/quotes themselves. All calls stay
 * disabled until `enabled` is true, so mounting one of these per pipeline
 * row costs nothing until its popover is opened.
 */
export function useJobInvoiceBreakdown(jobId: string, enabled: boolean) {
  const jobQuery = useQuery(getJob, create(GetJobRequestSchema, { jobId }), {
    enabled: enabled && !!jobId,
  })

  const invoiceIds = jobQuery.data?.job?.invoiceIds ?? []
  const quoteIds = jobQuery.data?.job?.quoteIds ?? []

  const invoicesQuery = useQuery(
    getInvoiceLitesByIds,
    create(GetInvoiceLitesByIdsRequestSchema, { ids: invoiceIds }),
    { enabled: enabled && jobQuery.isSuccess && invoiceIds.length > 0 },
  )

  const quotesQuery = useQuery(
    getQuoteLitesByIds,
    create(GetQuoteLitesByIdsRequestSchema, { ids: quoteIds }),
    { enabled: enabled && jobQuery.isSuccess && quoteIds.length > 0 },
  )

  const isLoading =
    enabled &&
    (jobQuery.isLoading ||
      (jobQuery.isSuccess &&
        ((invoiceIds.length > 0 && invoicesQuery.isLoading) || (quoteIds.length > 0 && quotesQuery.isLoading))))
  const isError = jobQuery.isError || invoicesQuery.isError || quotesQuery.isError
  const error = jobQuery.error || invoicesQuery.error || quotesQuery.error
  const permissionDenied = isError && !!error && ConnectError.from(error).code === Code.PermissionDenied

  return {
    invoices: invoicesQuery.data?.invoices ?? [],
    quotes: quotesQuery.data?.quotes ?? [],
    isLoading,
    isError,
    error,
    permissionDenied,
  }
}

/**
 * Lazily loads one job's events via `ListJobEvents` — disabled until
 * `enabled` is true, so mounting one of these per pipeline row costs nothing
 * until its Events popover is opened.
 */
export function useJobEventsBreakdown(jobId: string, enabled: boolean) {
  const query = useQuery(listJobEvents, create(ListJobEventsRequestSchema, { jobId }), {
    enabled: enabled && !!jobId,
  })

  const isError = query.isError
  const error = query.error
  const permissionDenied = isError && !!error && ConnectError.from(error).code === Code.PermissionDenied

  return {
    events: query.data?.events ?? [],
    isLoading: enabled && query.isLoading,
    isError,
    error,
    permissionDenied,
  }
}

/**
 * Renames a job (`UpdateJob` with a `title`-only field mask). Patches the
 * single-job cache with the server response and the job's title in any
 * `ListJobs` caches (table/board), then invalidates `ListJobs` so board
 * columns and summaries stay consistent.
 */
export function useUpdateJobTitle() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listJobsKey = createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined })

  const mutation = useMutation(updateJob, {
    onSuccess: (response) => {
      const updatedJob = response.job
      if (!updatedJob) return

      const queryKey = createConnectQueryKey({
        schema: getJob,
        transport,
        input: { jobId: updatedJob.id },
        cardinality: 'finite',
      })
      queryClient.setQueryData(queryKey, create(GetJobResponseSchema, { job: updatedJob }))

      queryClient.setQueriesData({ queryKey: listJobsKey }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        const data = old as { jobs?: Array<Record<string, unknown>> }
        if (!data.jobs) return old
        return {
          ...data,
          jobs: data.jobs.map((job) => (job['id'] === updatedJob.id ? { ...job, title: updatedJob.title } : job)),
        }
      })

      queryClient.invalidateQueries({ queryKey: listJobsKey })
    },
  })

  return {
    updateJobTitle: (jobId: string, title: string) =>
      mutation.mutateAsync(
        create(UpdateJobRequestSchema, { jobId, title, updateMask: create(FieldMaskSchema, { paths: ['title'] }) }),
      ),
    isPending: mutation.isPending,
  }
}

type JobsListCache = ListJobsResponse | InfiniteData<ListJobsResponse>

function timestampMillis(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return undefined
  const timestamp = value as { seconds?: bigint | number; nanos?: number }
  if (timestamp.seconds === undefined) return undefined
  return Number(timestamp.seconds) * 1000 + Math.floor((timestamp.nanos ?? 0) / 1_000_000)
}

/**
 * Whether a job (at a hypothetical `board_stage`) would appear in a cached
 * `ListJobs` query, given that query's own filters — read from its query-key
 * `input.filters` (connect-query keys only serialize SET fields). Every
 * Pipeline `ListJobs` call this app makes implicitly has `ExcludeNonOpen`
 * true server-side (`buildJobListFilters` never sets `filters.statuses`, see
 * `applyJobListSQLPaging` in `handler_grpc_job.go`), so an ended lead
 * (`isEnded`) matches nothing — it's excluded from every cache, not just
 * board-stage-filtered ones.
 */
export function jobMatchesListInput(
  job: JobLite,
  input: Record<string, unknown> | undefined,
  boardStage: JobBoardStageEnum,
  isEnded: boolean,
): boolean {
  if (isEnded) return false
  const filters = input && typeof input.filters === 'object' ? (input.filters as Record<string, unknown>) : undefined
  if (!filters) return true

  const boardStages = Array.isArray(filters.boardStages) ? (filters.boardStages as number[]) : []
  if (boardStages.length > 0 && !boardStages.includes(boardStage)) return false

  const assignedToId = typeof filters.assignedToId === 'string' ? filters.assignedToId : ''
  if (assignedToId && (job.assignedTo ?? '') !== assignedToId) return false

  const search = typeof filters.search === 'string' ? filters.search.toLowerCase() : ''
  if (search) {
    const firstName = job.contactFirstName ?? ''
    const lastName = job.contactLastName ?? ''
    const companyName = job.contactCompanyName ?? ''
    const values = [firstName, lastName, `${firstName} ${lastName}`, companyName, job.title]
    if (!values.some((value) => value.toLowerCase().includes(search))) return false
  }

  const eventStart = timestampMillis(filters.eventStart)
  const lastEventDate = timestampMillis(job.lastEventDate)
  if (eventStart !== undefined && (lastEventDate === undefined || lastEventDate < eventStart)) return false
  const eventEnd = timestampMillis(filters.eventEnd)
  const firstEventDate = timestampMillis(job.firstEventDate)
  if (eventEnd !== undefined && (firstEventDate === undefined || firstEventDate > eventEnd)) return false

  return true
}

/**
 * Board-stage a lead-phase job carries for a given inquiry stage — mirrors
 * `job_sql.go`'s `board_stage` CASE: only New maps to LeadNew, every other
 * stage (including Stale/Rejected, which then get filtered out of every
 * cache entirely — see {@link jobMatchesListInput}) maps to LeadInProgress.
 */
export function jobBoardStageForInquiryStage(stage: InquiryStageEnum): JobBoardStageEnum {
  return stage === InquiryStageEnum.INQUIRY_STAGE_NEW
    ? JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_NEW
    : JobBoardStageEnum.JOB_BOARD_STAGE_LEAD_IN_PROGRESS
}

/** Stale, Rejected and Referred end a lead: its job leaves the Open board. */
export function isEndedInquiryStage(stage: InquiryStageEnum): boolean {
  return (
    stage === InquiryStageEnum.INQUIRY_STAGE_STALE ||
    stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED ||
    stage === InquiryStageEnum.INQUIRY_STAGE_REFERRED
  )
}

/**
 * `JobStatusEnum` a lead-phase job carries for a given inquiry stage —
 * mirrors `deriveJobStatus` in `job_state.go` for a job with no booking yet
 * (always true for a job still in `JOB_PHASE_NEW`).
 */
export function jobStatusForInquiryStage(stage: InquiryStageEnum): JobStatusEnum {
  if (stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED || stage === InquiryStageEnum.INQUIRY_STAGE_REFERRED) {
    return JobStatusEnum.JOB_STATUS_LOST
  }
  if (stage === InquiryStageEnum.INQUIRY_STAGE_STALE) return JobStatusEnum.JOB_STATUS_STALE
  return JobStatusEnum.JOB_STATUS_OPEN
}

/**
 * Moves a lead-phase job between board-stage-scoped `ListJobs` caches
 * (Pipeline board columns, and the Pipeline table's single unfiltered cache)
 * when its inquiry's stage changes — same Pass 1 (locate the row)/Pass 2
 * (classify every cache as source/destination/both/neither and patch)
 * approach as `moveInquiryStageInListCaches` in `leads.store.ts`.
 *
 * Returns the row's previous cache state (before this move), or `undefined`
 * when the job isn't cached in any `ListJobs` query — callers use this to
 * skip the `GetPipelineSummary` bucket patch too, since there's nothing to
 * derive the previous board stage/`quoted_cents` from.
 */
export function moveJobBoardStageInListCaches(
  queryClient: QueryClient,
  listQueryKey: QueryKey,
  inquiryId: string,
  newStage: InquiryStageEnum,
): JobLite | undefined {
  const isEnded = isEndedInquiryStage(newStage)
  const newBoardStage = jobBoardStageForInquiryStage(newStage)

  const caches = queryClient.getQueriesData<JobsListCache>({ queryKey: listQueryKey })

  // Pass 1 — locate the full row anywhere it's cached.
  let movedRow: JobLite | undefined
  for (const [, data] of caches) {
    if (!data) continue
    const pages = 'pages' in data ? data.pages : [data]
    for (const page of pages) {
      const row = page.jobs?.find((job) => job.inquiryId === inquiryId)
      if (row) {
        movedRow = row
        break
      }
    }
    if (movedRow) break
  }
  if (!movedRow) return undefined

  const row = movedRow

  // Pass 2 — classify each cache and apply the move.
  caches.forEach(([queryKey, data]) => {
    if (!data) return

    const meta = queryKey[1] as { input?: Record<string, unknown> | 'skipped' } | undefined
    const input = meta?.input && meta.input !== 'skipped' ? meta.input : undefined
    const hasFrom = jobMatchesListInput(row, input, row.boardStage, false)
    const hasTo = jobMatchesListInput(row, input, newBoardStage, isEnded)

    if (!hasFrom && !hasTo) return

    if (hasFrom && hasTo) {
      // Multi-stage or unfiltered cache (the Pipeline table): in-place
      // board_stage patch where the row is present; totalCount unchanged.
      if ('pages' in data) {
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) =>
            create(ListJobsResponseSchema, {
              jobs: page.jobs.map((job) =>
                job.inquiryId === inquiryId ? { ...job, boardStage: newBoardStage } : job,
              ),
              totalCount: page.totalCount,
            }),
          ),
        })
        return
      }
      queryClient.setQueryData(
        queryKey,
        create(ListJobsResponseSchema, {
          jobs: (data.jobs ?? []).map((job) =>
            job.inquiryId === inquiryId ? { ...job, boardStage: newBoardStage } : job,
          ),
          totalCount: data.totalCount,
        }),
      )
      return
    }

    if (hasFrom) {
      // Source board-stage column: drop the row, decrement totalCount on
      // every page.
      if ('pages' in data) {
        queryClient.setQueryData(queryKey, {
          ...data,
          pages: data.pages.map((page) =>
            create(ListJobsResponseSchema, {
              jobs: page.jobs.filter((job) => job.inquiryId !== inquiryId),
              totalCount: Math.max(0, page.totalCount - 1),
            }),
          ),
        })
        return
      }
      queryClient.setQueryData(
        queryKey,
        create(ListJobsResponseSchema, {
          jobs: (data.jobs ?? []).filter((job) => job.inquiryId !== inquiryId),
          totalCount: Math.max(0, data.totalCount - 1),
        }),
      )
      return
    }

    // Destination board-stage column: bump totalCount on every page. Prepend
    // the moved row to the first page of infinite caches only.
    if ('pages' in data) {
      const alreadyPresent = data.pages.some((page) => page.jobs.some((job) => job.inquiryId === inquiryId))
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data.pages.map((page, pageIndex) =>
          create(ListJobsResponseSchema, {
            jobs: pageIndex === 0 && !alreadyPresent ? [{ ...row, boardStage: newBoardStage }, ...page.jobs] : page.jobs,
            totalCount: page.totalCount + 1,
          }),
        ),
      })
      return
    }
    queryClient.setQueryData(
      queryKey,
      create(ListJobsResponseSchema, { jobs: data.jobs ?? [], totalCount: data.totalCount + 1 }),
    )
  })

  return row
}

/**
 * Adjusts `GetPipelineSummary`'s `stage_buckets` (and, for an ended lead,
 * `buckets`' NEW-phase entry) for a lead-stage move — `previousRow` is the
 * job's cache state from just before {@link moveJobBoardStageInListCaches}
 * moved it, which supplies both the previous board stage and `quoted_cents`.
 * A no-op when the move didn't change board stage (e.g. reselecting the same
 * stage).
 */
export function patchPipelineSummaryForLeadMove(
  queryClient: QueryClient,
  summaryQueryKey: QueryKey,
  previousRow: JobLite,
  newStage: InquiryStageEnum,
): void {
  const isEnded = isEndedInquiryStage(newStage)
  const newBoardStage = jobBoardStageForInquiryStage(newStage)
  const previousBoardStage = previousRow.boardStage
  if (previousBoardStage === newBoardStage && !isEnded) return

  const quotedCents = previousRow.quotedCents ?? 0n
  const caches = queryClient.getQueriesData<GetPipelineSummaryResponse>({ queryKey: summaryQueryKey })
  caches.forEach(([queryKey, data]) => {
    if (!data) return
    const stageBuckets = data.stageBuckets.map((bucket) => {
      if (bucket.stage === previousBoardStage) {
        return { ...bucket, count: Math.max(0, bucket.count - 1), quotedCents: bucket.quotedCents - quotedCents }
      }
      if (!isEnded && bucket.stage === newBoardStage) {
        return { ...bucket, count: bucket.count + 1, quotedCents: bucket.quotedCents + quotedCents }
      }
      return bucket
    })
    const buckets = isEnded
      ? data.buckets.map((bucket) =>
          bucket.bucket === JobPhaseEnum.JOB_PHASE_NEW
            ? { ...bucket, count: Math.max(0, bucket.count - 1), quotedCents: bucket.quotedCents - quotedCents }
            : bucket,
        )
      : data.buckets
    queryClient.setQueryData(queryKey, create(GetPipelineSummaryResponseSchema, { ...data, stageBuckets, buckets }))
  })
}

/**
 * Patches `GetJob`'s `status` for the job linked to `inquiryId` — the job
 * detail page's status badge. `Job` (unlike `JobLite`) has no `board_stage`
 * field, so there's nothing else on this response derived from the inquiry's
 * stage. Only patches a job still in `JOB_PHASE_NEW` (the only phase
 * {@link jobStatusForInquiryStage}'s mapping holds for, since a
 * booked/complete job's status no longer follows the inquiry stage — see
 * `deriveJobStatus` in `job_state.go`).
 */
export function patchGetJobForLeadMove(
  queryClient: QueryClient,
  getJobQueryKey: QueryKey,
  inquiryId: string,
  newStage: InquiryStageEnum,
): void {
  const newStatus = jobStatusForInquiryStage(newStage)
  const caches = queryClient.getQueriesData<GetJobResponse>({ queryKey: getJobQueryKey })
  caches.forEach(([queryKey, data]) => {
    const job = data?.job
    if (!job || job.inquiryId !== inquiryId || job.phase !== JobPhaseEnum.JOB_PHASE_NEW) return
    queryClient.setQueryData(queryKey, create(GetJobResponseSchema, { job: { ...job, status: newStatus } }))
  })
}

/**
 * Moves a lead between the Pipeline board's New/In Progress/Stale/Rejected
 * stages by updating the underlying inquiry's stage. `updateInquiryStage`
 * (from `useUpdateInquiry`) already optimistically patches `GetInquiry` and
 * `ListInquiries` (and rolls them back on error) — this wraps that call with
 * the Pipeline-specific caches it doesn't know about: `ListJobs` board/table
 * caches, `GetPipelineSummary`'s bucket counts, and the linked `GetJob`.
 * Structured the same way a `useMutation`'s onMutate/onError/onSettled would
 * be (cancel → snapshot via `getQueriesData` → patch; restore snapshots on
 * error; invalidate on settle), just inlined around the existing mutation
 * rather than a second one, so `GetInquiry`/`ListInquiries` keep their one
 * source of truth.
 */
export function useMoveLeadStage() {
  const { updateInquiryStage, isLoading } = useUpdateInquiry()
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listJobsKey = createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined })
  const pipelineSummaryKey = createConnectQueryKey({ schema: getPipelineSummary, transport, cardinality: undefined })
  const getJobKey = createConnectQueryKey({ schema: getJob, transport, cardinality: undefined })

  const moveLeadStage = async (
    inquiryId: string,
    stage: InquiryStageEnum,
    rejectReason?: InquiryRejectReasonEnum,
    rejectNotes?: string,
  ) => {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: listJobsKey }),
      queryClient.cancelQueries({ queryKey: pipelineSummaryKey }),
      queryClient.cancelQueries({ queryKey: getJobKey }),
    ])

    const previousJobsListCaches = queryClient.getQueriesData<JobsListCache>({ queryKey: listJobsKey })
    const previousSummaryCaches = queryClient.getQueriesData({ queryKey: pipelineSummaryKey })
    const previousGetJobCaches = queryClient.getQueriesData({ queryKey: getJobKey })

    const previousRow = moveJobBoardStageInListCaches(queryClient, listJobsKey, inquiryId, stage)
    if (previousRow) {
      patchPipelineSummaryForLeadMove(queryClient, pipelineSummaryKey, previousRow, stage)
    }
    patchGetJobForLeadMove(queryClient, getJobKey, inquiryId, stage)

    try {
      await updateInquiryStage(inquiryId, stage, rejectReason, rejectNotes)
    } catch (error) {
      previousJobsListCaches.forEach(([key, data]) => queryClient.setQueryData(key, data))
      previousSummaryCaches.forEach(([key, data]) => queryClient.setQueryData(key, data))
      previousGetJobCaches.forEach(([key, data]) => queryClient.setQueryData(key, data))
      throw error
    } finally {
      void queryClient.invalidateQueries({ queryKey: listJobsKey, refetchType: 'active' })
      void queryClient.invalidateQueries({ queryKey: pipelineSummaryKey, refetchType: 'active' })
      void queryClient.invalidateQueries({ queryKey: getJobKey, refetchType: 'active' })
    }
  }

  return { moveLeadStage, isPending: isLoading }
}

/**
 * Returns a function that invalidates every cached `ListJobActivity` query
 * (any job, filter, or page) so the job Activity card refetches after a
 * mutation that adds to the feed.
 */
export function useInvalidateJobActivity() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  return () =>
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({ schema: listJobActivity, transport, cardinality: undefined }),
    })
}

/**
 * Returns a function that invalidates the job-level caches a quote change
 * moves: the job (phase, status, quote list), its events, its activity feed,
 * the Pipeline lists, and the board's summary counts. For quote edits made off
 * the job page, so returning to the job or the board shows the change.
 */
export function useInvalidateJobAfterQuoteChange() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const invalidate = (queryKey: QueryKey) => queryClient.invalidateQueries({ queryKey })
  return () =>
    Promise.all([
      invalidate(createConnectQueryKey({ schema: getJob, transport, cardinality: undefined })),
      invalidate(createConnectQueryKey({ schema: listJobEvents, transport, cardinality: undefined })),
      invalidate(createConnectQueryKey({ schema: listJobActivity, transport, cardinality: undefined })),
      invalidate(createConnectQueryKey({ schema: listJobs, transport, cardinality: undefined })),
      invalidate(createConnectQueryKey({ schema: getPipelineSummary, transport, cardinality: undefined })),
    ])
}
